import "./styles.css";

import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { ChangeEvent } from "react";

import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import { sxPagination, sxPaginationContrast } from "../../../assets/styles/CommonSxProps";
import LoadingApp from "../../../components/LoadingApp";
import ModalFeedBack from "../../../components/ModalFeedBack";
import { Conteudo } from "../../../interfaces/entities/SaudeBemEstar/Conteudos/Conteudo";
import { getConteudos } from "../../../services/SaudeBemEstar/ConteudosService";
import { removerAcentos } from "../../../utils/RemoverAcentos";
import ConteudoCard from "./ConteudoCard";
import { sxTextFieldContrast } from "./sxProps";
import VoltarButton from "../../../components/Accessibility/VoltarButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import DefaultTextField from "../../../components/DefaultTextField";
import PageTitle from "../../../components/Accessibility/PageTitle";

const invertDate = (value: string) => {
  if (!value) return value;
  const [date, time] = value.split(" ");
  const [day, month, year] = date?.split("/");
  return `${year}-${month}-${day} ${time}`;
};

export default function Conteudos() {
  const rowsPerPage = 5;
  const navigate = useNavigate()
  const [searchParams] = useSearchParams();
  const back = searchParams.get('back');

  const [loading, setLoading] = React.useState(false);
  const [openErrorModal, setOpenErrorModal] = React.useState(false);
  const [conteudos, setConteudos] = React.useState<Conteudo[]>([]);
  const [search, setSearch] = React.useState<string>("");
  const [page, setPage] = React.useState(1);

  const accessibility = useAccessibility();

  const closeErrorModal = () => setOpenErrorModal(false);

  React.useEffect(() => {
    setLoading(true);
    getConteudos()
      .then((conteudos) => {
        conteudos?.sort((a, b) => {
          const aDate = invertDate(a.data);
          const bDate = invertDate(b.data);
          if (aDate < bDate) return 1;
          else if (aDate > bDate) return -1;
          else return 0;
        });
        setConteudos(conteudos);
      })
      .catch(() => setOpenErrorModal(true))
      .finally(() => setLoading(false));
  }, []);

  const searchValue = removerAcentos(search?.toLowerCase());
  const conteudosFiltrado = conteudos?.filter((conteudo) => {
    if (removerAcentos(conteudo.data)?.includes(searchValue)) return true;
    if (removerAcentos(conteudo.titulo?.toLowerCase())?.includes(searchValue)) return true;
    if (removerAcentos(conteudo.conteudo?.toLowerCase())?.includes(searchValue)) return true;
    return false;
  });
  const normalizePage = Math.min(page, Math.ceil(conteudosFiltrado?.length / rowsPerPage));
  const startIndex = (normalizePage - 1) * rowsPerPage;
  const endIndex = normalizePage * rowsPerPage;
  const conteudoElements = conteudosFiltrado?.slice(startIndex, endIndex).map((conteudo, index) => {
    return <ConteudoCard conteudo={conteudo} key={conteudo.id} indice={index} contentListLenght={conteudosFiltrado?.length} />;
  });
  const emptyMessage = (
    <div>
      <Typography style={accessibility.textStyleAccessibilityWithMargin}>Nenhum conteúdo encontrado.</Typography>
      <Typography style={accessibility.textStyleAccessibility}>Você será notificado quando receber novos conteúdos.</Typography>
    </div>
  );

  const scrollTop = () => {
    const element = document.getElementById('conteudos-content');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    scrollTop();
  };

  return (
    <>
      <div className="conteudos-content">
        <PageTitle text="Conteúdos" />
        <div className="conteudo-search">
          <label id="scroll-ref" htmlFor="pesquisar" className="select-label" style={accessibility.textStyleAccessibility}>
            Pesquisar por título, tema do conteúdo ou data
          </label>
          <DefaultTextField 
            handleChange={(event) => setSearch(event.target.value)}
            value={search}
            placeholder="Exemplos: Terapia (título ou tema do conteúdo) ou 16/10/2024 (data)"
          />
        </div>
        {conteudosFiltrado?.length ? <div>{conteudoElements}</div> : emptyMessage}
        {conteudosFiltrado?.length > rowsPerPage && (
          <Pagination
            count={Math.ceil(conteudosFiltrado.length / rowsPerPage)}
            sx={accessibility.contrastMode ? sxPaginationContrast : sxPagination}
            hideNextButton
            hidePrevButton
            color="primary"
            page={normalizePage}
            onChange={handlePageChange}
          />
        )}
        {back === 's' &&
          <VoltarButton onClick={() => navigate(-1)} />}
      </div>
      <LoadingApp showLoading={loading} />
      <ModalFeedBack
        showModal={openErrorModal}
        handleShowModal={closeErrorModal}
        handleButtonModal={closeErrorModal}
        icon="error"
        title="Erro"
        subTitle="Ocorreu um erro. Tente novamente em instantes."
        textButton="Fechar"
        showButtonModal
      />
    </>
  );
}
